import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
    DialogBody, DialogFooter, DialogCloseButton,
} from "../../components/ui/dialog";

const SEVERITY_STYLES = {
    info: {
        wrapper: "bg-blue-50 border-blue-200",
        icon:    "text-blue-500",
        title:   "text-blue-900",
        body:    "text-blue-800",
    },
    warning: {
        wrapper: "bg-amber-50 border-amber-200",
        icon:    "text-amber-500",
        title:   "text-amber-900",
        body:    "text-amber-800",
    },
    error: {
        wrapper: "bg-red-50 border-red-200",
        icon:    "text-red-500",
        title:   "text-red-900",
        body:    "text-red-800",
    },
    success: {
        wrapper: "bg-green-50 border-green-200",
        icon:    "text-green-500",
        title:   "text-green-900",
        body:    "text-green-800",
    },
};

const SeverityIcon = ({ severity }) => {
    const cls = (SEVERITY_STYLES[severity] || SEVERITY_STYLES.info).icon + " w-5 h-5 shrink-0 mt-0.5";
    if (severity === "warning" || severity === "error") {
        return (
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={cls} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
            </svg>
        );
    }
    if (severity === "success") {
        return (
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={cls} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
            </svg>
        );
    }
    return (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={cls} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
        </svg>
    );
};

function ConfirmDialog({ action, notificationId, open, onClose, onConfirm }) {
    const [loading, setLoading] = useState(false);

    const handleConfirm = async () => {
        setLoading(true);
        try {
            await onConfirm(notificationId, action);
            toast.success("Akce byla úspěšně provedena.");
            onClose();
        } catch (err) {
            toast.error("Nepodařilo se provést akci. Zkuste to prosím znovu.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent size="sm" className="relative">
                <DialogCloseButton />
                <DialogHeader>
                    <DialogTitle>{action.confirmTitle || "Potvrdit akci"}</DialogTitle>
                </DialogHeader>
                <DialogBody>
                    <div className="space-y-3 text-sm text-foreground leading-relaxed">
                        {(action.confirmBody || "").split("\n").map((line, i) =>
                            line.trim() === "" ? null : (
                                <p key={i}>{line.replace(/^- /, "• ")}</p>
                            )
                        )}
                    </div>
                </DialogBody>
                <DialogFooter>
                    <div className="flex items-center gap-2 justify-end w-full">
                        <Button variant="outline" onClick={onClose} disabled={loading}>Zrušit</Button>
                        <Button onClick={handleConfirm} disabled={loading}>
                            {loading ? "Ukládám…" : action.label}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function NotificationBanner({ notification, onExecute, onDismiss }) {
    const styles = SEVERITY_STYLES[notification.severity] || SEVERITY_STYLES.info;
    const [confirmAction, setConfirmAction] = useState(null);

    const handleActionClick = (action) => {
        if (action.confirmRequired) {
            setConfirmAction(action);
        }
    };

    return (
        <>
            <div
                role="alert"
                className={`rounded-xl border px-5 py-4 ${styles.wrapper}`}
            >
                <div className="flex gap-3">
                    <SeverityIcon severity={notification.severity} />
                    <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold mb-1 ${styles.title}`}>
                            {notification.title}
                        </p>
                        <p className={`text-sm leading-relaxed ${styles.body}`}>
                            {notification.body}
                        </p>

                        {notification.actions && notification.actions.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-3">
                                {notification.actions.map((action) => {
                                    if (action.href) {
                                        return (
                                            <Button
                                                key={action.id}
                                                asChild
                                                size="sm"
                                                variant={action.variant === "outline" ? "outline" : "default"}
                                            >
                                                <Link to={action.href}>{action.label}</Link>
                                            </Button>
                                        );
                                    }
                                    return (
                                        <Button
                                            key={action.id}
                                            size="sm"
                                            variant={action.variant === "outline" ? "outline" : "default"}
                                            onClick={() => handleActionClick(action)}
                                        >
                                            {action.label}
                                        </Button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {notification.dismissible && (
                        <button
                            onClick={() => onDismiss(notification._id)}
                            aria-label="Zavřít upozornění"
                            className={`shrink-0 p-1 rounded hover:bg-black/10 transition-colors ${styles.icon}`}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}
                </div>
            </div>

            {confirmAction && (
                <ConfirmDialog
                    action={confirmAction}
                    notificationId={notification._id}
                    open={!!confirmAction}
                    onClose={() => setConfirmAction(null)}
                    onConfirm={onExecute}
                />
            )}
        </>
    );
}

/**
 * Renders all notifications for a given page.
 * Usage: <NotificationBanners page="profile" notifications={...} executeAction={...} dismiss={...} />
 */
export default function NotificationBanners({ page, notifications, executeAction, dismiss }) {
    const visible = notifications.filter(
        (n) => !n.showOnPages || n.showOnPages.length === 0 || n.showOnPages.includes(page)
    );
    if (visible.length === 0) return null;
    return (
        <div className="space-y-3 mb-6">
            {visible.map((n) => (
                <NotificationBanner
                    key={n._id}
                    notification={n}
                    onExecute={executeAction}
                    onDismiss={dismiss}
                />
            ))}
        </div>
    );
}
